import { createContext, useContext, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import {
  useGetPendingTransactions,
  GET_PENDING_TRANSACTIONS_QUERY_KEY,
} from "../../../queries/useGetPendingTransactions";
import { useGetProcessedTransactions } from "../../../queries/useGetProcessedTransactions";
import {
  useGetInProgressTransactions,
  GET_IN_PROGRESS_TRANSACTIONS_QUERY_KEY,
} from "../../../queries/useGetInProgressTransactions";
import { useGetTransactionDetails } from "../../../queries/useGetTransactionDetails";
import { useApprovePendingTransaction } from "../../../queries/useApprovePendingTransaction";
import { useDeclinePendingTransaction } from "../../../queries/useDeclinePendingTransaction";
import { useRefundPendingTransaction } from "../../../queries/useRefundPendingTransaction";
import { useDownloadWithdrawalReview } from "../../../queries/useDownloadWithdrawalReview";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { triggerBrowserDownload } from "../../../utils/triggerBrowserDownload";
import { getAdminNow } from "../../../utils/dateService";
import {
  removeTransactionFromActiveWithdrawalCaches,
  restoreWithdrawalCacheSnapshots,
} from "../withdrawalReviewOptimisticCache";
const TAB_TO_LIFECYCLE = {
  PENDING: "pending",
  IN_PROGRESS: "in-progress",
  PROCESSED: "processed",
};

const WithdrawalReviewContext = createContext(null);

export const WithdrawalReviewProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const todayRange = [getAdminNow().subtract(6, "day"), getAdminNow()];
  const [rangeDates, setRangeDates] = useState(todayRange);
  const [selectedTab, setSelectedTab] = useState("PENDING");
  const [subregionFilter, setSubregionFilterState] = useState("");
  const [methodFilter, setMethodFilterState] = useState("");

  const [pendingPageSize, setPendingPageSize] = useState(100);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingTotalCount, setPendingTotalCount] = useState(null);
  const [pendingLastKey, setPendingLastKey] = useState(null);

  const [processedPageSize, setProcessedPageSize] = useState(100);
  const [processedCurrentPage, setProcessedCurrentPage] = useState(1);
  const [processedTotalCount, setProcessedTotalCount] = useState(null);
  const [processedLastKey, setProcessedLastKey] = useState(null);

  const [inProgressPageSize, setInProgressPageSize] = useState(100);
  const [inProgressCurrentPage, setInProgressCurrentPage] = useState(1);
  const [inProgressTotalCount, setInProgressTotalCount] = useState(null);
  const [inProgressLastKey, setInProgressLastKey] = useState(null);

  const setSubregionFilter = (next) => {
    const value = next ? String(next) : "";
    setSubregionFilterState(value);
    // Reset paginators so each tab fetches the new filter from page 1.
    setPendingCurrentPage(1);
    setPendingLastKey(null);
    setProcessedCurrentPage(1);
    setProcessedLastKey(null);
    setInProgressCurrentPage(1);
    setInProgressLastKey(null);
  };

  const setMethodFilter = (next) => {
    const value = next ? String(next) : "";
    setMethodFilterState(value);
    setPendingCurrentPage(1);
    setPendingLastKey(null);
    setProcessedCurrentPage(1);
    setProcessedLastKey(null);
    setInProgressCurrentPage(1);
    setInProgressLastKey(null);
  };

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isPaymentDetailsOpen, setIsPaymentDetailsOpen] = useState(false);
  const [selectedPaymentRecord, setSelectedPaymentRecord] = useState(null);
  const [recordDeclined, setRecordDeclined] = useState(null);
  const [recordApproved, setRecordApproved] = useState(null);
  const [recordRefunded, setRecordRefunded] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [approvingTransactionId, setApprovingTransactionId] = useState(null);

  const subregionParam = subregionFilter || null;
  const methodParam = methodFilter || null;

  const {
    data: pendingTransactions,
    refetch: refetchPending,
    isLoading: loadingPending,
  } = useGetPendingTransactions(
    rangeDates[0]?.format("YYYY-MM-DD"),
    rangeDates[1]?.format("YYYY-MM-DD"),
    pendingPageSize,
    pendingCurrentPage,
    pendingLastKey,
    pendingTotalCount,
    subregionParam,
    methodParam
  );

  const {
    data: processedTransactions,
    refetch: refetchProcessed,
    isLoading: loadingProcessed,
  } = useGetProcessedTransactions(
    rangeDates[0]?.format("YYYY-MM-DD"),
    rangeDates[1]?.format("YYYY-MM-DD"),
    processedPageSize,
    processedCurrentPage,
    processedLastKey,
    processedTotalCount,
    subregionParam,
    methodParam
  );

  const {
    data: inProgressTransactions,
    refetch: refetchInProgress,
    isLoading: loadingInProgress,
  } = useGetInProgressTransactions(
    rangeDates[0]?.format("YYYY-MM-DD"),
    rangeDates[1]?.format("YYYY-MM-DD"),
    inProgressPageSize,
    inProgressCurrentPage,
    inProgressLastKey,
    inProgressTotalCount,
    subregionParam,
    methodParam
  );

  const { mutateAsync: getTransactionDetails } = useGetTransactionDetails();
  const {
    mutateAsync: approvePendingTransaction,
    isPending: approveTransactionLoading,
  } = useApprovePendingTransaction();
  const {
    mutateAsync: declineTransaction,
    isPending: declineTransactionLoading,
  } = useDeclinePendingTransaction();
  const {
    mutateAsync: refundTransaction,
    isPending: refundTransactionLoading,
  } = useRefundPendingTransaction();
  const {
    mutateAsync: downloadWithdrawalReviewCsv,
    isPending: isDownloading,
  } = useDownloadWithdrawalReview();

  const handleDownloadCsv = async () => {
    const lifecycle = TAB_TO_LIFECYCLE[selectedTab];
    if (!lifecycle) return;

    try {
      const result = await downloadWithdrawalReviewCsv({
        lifecycle,
        start_date: rangeDates[0]?.format("YYYY-MM-DD"),
        end_date: rangeDates[1]?.format("YYYY-MM-DD"),
        ...(subregionParam && { subregion: subregionParam }),
        ...(methodParam && { method: methodParam }),
      });

      if (!result?.download_url) {
        message.error(AppStrings.WITHDRAWAL_EXPORT_FAILED);
        return;
      }

      triggerBrowserDownload(result.download_url);

      if (result.truncated) {
        message.warning(AppStrings.WITHDRAWAL_EXPORT_TRUNCATED);
      }
    } catch (error) {
      console.error("error downloading withdrawal review export", error);
      message.error(
        getApiErrorMessage(error, AppStrings.WITHDRAWAL_EXPORT_FAILED),
      );
    }
  };

  useEffect(() => {
    const nextLastKey = pendingTransactions?.last_evaluated_key ?? null;
    setPendingLastKey(pendingPageSize === 1 ? null : nextLastKey);

    const totalResults = pendingTransactions?.pagination?.total_results;
    if (typeof totalResults === "number") {
      setPendingTotalCount(totalResults);
    }
  }, [pendingTransactions, pendingPageSize]);

  useEffect(() => {
    const nextLastKey = processedTransactions?.last_evaluated_key ?? null;
    setProcessedLastKey(processedPageSize === 1 ? null : nextLastKey);

    const totalResults = processedTransactions?.pagination?.total_results;
    if (typeof totalResults === "number") {
      setProcessedTotalCount(totalResults);
    }
  }, [processedTransactions, processedPageSize]);

  useEffect(() => {
    const nextLastKey = inProgressTransactions?.last_evaluated_key ?? null;
    setInProgressLastKey(inProgressPageSize === 1 ? null : nextLastKey);

    const totalResults = inProgressTransactions?.pagination?.total_results;
    if (typeof totalResults === "number") {
      setInProgressTotalCount(totalResults);
    }
  }, [inProgressTransactions, inProgressPageSize]);

  const handleViewUser = ({ user_id }) => {
    const url = `${NAVIGATION_ROUTES.USER_MANAGER}?userId=${user_id}`;
    window.open(url, "_blank");
  };

  // Opening the User Overview from a pending row must start the review the same
  // way the eyeball does: get-transaction-details starts the timer, assigns the
  // agent, and moves the row to In Progress server-side. Tracking is intentionally
  // implicit, so this fires silently (no modal, no toast) and never blocks the tab.
  const handleViewUserFromPending = (record) => {
    getTransactionDetails({
      user_id: record.user_id,
      transaction_id: record.transaction_id,
    })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [GET_PENDING_TRANSACTIONS_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [GET_IN_PROGRESS_TRANSACTIONS_QUERY_KEY],
        });
      })
      .catch((error) => {
        console.error("error starting review from username click", error);
      });
    handleViewUser(record);
  };

  const handleApproved = async ({
    user_id,
    transaction_id,
    reason,
  }) => {
    const previous = removeTransactionFromActiveWithdrawalCaches(
      queryClient,
      transaction_id,
    );
    setIsApproveModalOpen(false);
    setRecordApproved(null);
    try {
      setApprovingTransactionId(transaction_id);
      await approvePendingTransaction({
        user_id,
        transaction_id,
        reason,
      });
      void Promise.all([
        refetchPending(),
        refetchProcessed(),
        refetchInProgress(),
      ]).catch((error) => {
        console.error("error refetching after approve", error);
      });
    } catch (error) {
      restoreWithdrawalCacheSnapshots(queryClient, previous);
      console.error("error approving transaction", error);
      message.error(
        getApiErrorMessage(error, AppStrings.ERROR_PROCESSING_REQUEST)
      );
      throw error;
    } finally {
      setApprovingTransactionId(null);
    }
  };

  const openApproveModal = (record) => {
    setRecordApproved(record);
    setIsApproveModalOpen(true);
  };

  const handleDecline = async ({ user_id, transaction_id, reason }) => {
    const previous = removeTransactionFromActiveWithdrawalCaches(
      queryClient,
      transaction_id,
    );
    setIsDeclineModalOpen(false);
    setRecordDeclined(null);
    try {
      await declineTransaction({ user_id, transaction_id, reason });
      void Promise.all([
        refetchPending(),
        refetchProcessed(),
        refetchInProgress(),
      ]).catch((error) => {
        console.error("error refetching after decline", error);
      });
    } catch (error) {
      restoreWithdrawalCacheSnapshots(queryClient, previous);
      console.error("error declining transaction", error);
      message.error(
        getApiErrorMessage(error, AppStrings.ERROR_PROCESSING_REQUEST)
      );
      throw error;
    }
  };

  const openDeclinedModal = (record) => {
    setRecordDeclined(record);
    setIsDeclineModalOpen(true);
  };

  const handleRefund = async ({
    user_id,
    transaction_id,
    refund_amount,
    notes,
    disclaimer_acknowledged,
  }) => {
    const previous = removeTransactionFromActiveWithdrawalCaches(
      queryClient,
      transaction_id,
    );
    try {
      const response = await refundTransaction({
        user_id,
        transaction_id,
        refund_amount,
        notes,
        disclaimer_acknowledged,
      });
      setIsRefundModalOpen(false);
      setRecordRefunded(null);
      void Promise.all([
        refetchPending(),
        refetchProcessed(),
        refetchInProgress(),
      ]).catch((error) => {
        console.error("error refetching after refund", error);
      });
      return response;
    } catch (error) {
      const isReviewConflict = error?.response?.status === 409;
      if (isReviewConflict) {
        setIsRefundModalOpen(false);
        setRecordRefunded(null);
        void Promise.all([
          refetchPending(),
          refetchProcessed(),
          refetchInProgress(),
        ]).catch((refetchError) => {
          console.error("error refetching after refund conflict", refetchError);
        });
      } else {
        restoreWithdrawalCacheSnapshots(queryClient, previous);
      }
      console.error("error refunding transaction", error);
      message.error(
        getApiErrorMessage(error, AppStrings.ERROR_PROCESSING_REQUEST),
      );
      throw error;
    }
  };

  const openRefundModal = (record) => {
    setRecordRefunded(record);
    setIsRefundModalOpen(true);
  };

  const closePaymentDetails = () => {
    setIsPaymentDetailsOpen(false);
    setSelectedPaymentRecord(null);
  };

  const openPaymentDetailsDrawer = (record) => {
    setSelectedPaymentRecord(record);
    setIsPaymentDetailsOpen(true);
  };

  const openDetailsModal = async ({ user_id, transaction_id, ...rest }) => {
    try {
      const result = await getTransactionDetails({ user_id, transaction_id });
      setUserDetails({ ...result, user_id, transaction_id, ...rest });
      setIsDetailsModalOpen(true);
      queryClient.invalidateQueries({
        queryKey: [GET_PENDING_TRANSACTIONS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_IN_PROGRESS_TRANSACTIONS_QUERY_KEY],
      });
    } catch (error) {
      console.error("error opening details modal", error);
      message.error(
        getApiErrorMessage(error, AppStrings.ERROR_PROCESSING_REQUEST)
      );
    }
  };

  return (
    <WithdrawalReviewContext.Provider
      value={{
        rangeDates,
        setRangeDates,

        subregionFilter,
        setSubregionFilter,
        methodFilter,
        setMethodFilter,

        selectedTab,
        setSelectedTab,

        pendingTransactions,
        processedTransactions,
        inProgressTransactions,
        loadingPending,
        loadingProcessed,
        loadingInProgress,

        pendingPageSize,
        setPendingPageSize,
        pendingCurrentPage,
        setPendingCurrentPage,
        pendingTotalCount,

        processedPageSize,
        setProcessedPageSize,
        processedCurrentPage,
        setProcessedCurrentPage,
        processedTotalCount,

        inProgressPageSize,
        setInProgressPageSize,
        inProgressCurrentPage,
        setInProgressCurrentPage,
        inProgressTotalCount,
        refetchInProgress,

        isDeclineModalOpen,
        setIsDeclineModalOpen,
        isApproveModalOpen,
        setIsApproveModalOpen,
        isRefundModalOpen,
        setIsRefundModalOpen,
        isDetailsModalOpen,
        setIsDetailsModalOpen,
        isPaymentDetailsOpen,
        selectedPaymentRecord,
        closePaymentDetails,
        openPaymentDetailsDrawer,
        recordDeclined,
        recordApproved,
        recordRefunded,
        userDetails,

        handleViewUser,
        handleViewUserFromPending,
        handleApproved,
        handleDecline,
        handleRefund,
        openDeclinedModal,
        openApproveModal,
        openRefundModal,
        openDetailsModal,

        declineTransactionLoading,
        approveTransactionLoading,
        refundTransactionLoading,
        approvingTransactionId,

        handleDownloadCsv,
        isDownloading,
      }}
    >
      {children}
    </WithdrawalReviewContext.Provider>
  );
};

export const useWithdrawalReview = () => useContext(WithdrawalReviewContext);
